import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/utils/helpers';
import { SignedInPill } from './SignedInPill';
import {
  LayoutDashboard, Building2, LogOut, ChevronLeft, ChevronRight, ChevronDown, Bell, Search, Menu,
  ToggleLeft, UserPlus, Banknote, ShieldAlert, ScrollText, HeartPulse,
  Megaphone, Mail, Layers, Sun, Moon, Table2, BarChart3, Database,
  MessageSquare, Palette, BookOpen, Sparkles, Server, DollarSign, Flag, Settings,
  ClipboardList, Smartphone,
} from 'lucide-react';

// ── Nav model ─────────────────────────────────────────────────────────
// Leaf entries link directly. Group entries expand to show children;
// auto-expand if any child is the current route.
type NavLeaf  = { label: string; href: string; icon: any };
type NavGroup = { label: string; icon: any; children: NavLeaf[] };
type NavItem  = NavLeaf | NavGroup;
const isGroup = (n: NavItem): n is NavGroup => 'children' in n;

const NAV: NavItem[] = [
  { label: 'Overview',        href: '/admin',                 icon: LayoutDashboard },
  { label: 'Analytics',       href: '/admin/analytics',       icon: BarChart3 },
  { label: 'Interview Log',   href: '/admin/interviews',      icon: ClipboardList },
  { label: 'Tenants',         href: '/admin/tenants',         icon: Building2 },

  // ── Marketing (parent) ──
  { label: 'Marketing', icon: UserPlus, children: [
    { label: 'Business Leads', href: '/admin/business-leads', icon: UserPlus },
  ]},

  // ── Plans (parent) ──
  { label: 'Plans', icon: Layers, children: [
    { label: 'Feature Gating',         href: '/admin/feature-gating',  icon: ToggleLeft },
    { label: 'Platform Plan Builder',  href: '/admin/plan-builder',    icon: Layers },
    { label: 'Platform Plans',         href: '/admin/platform-plans',  icon: Table2 },
  ]},

  { label: 'Feedback',        href: '/admin/feedback',        icon: MessageSquare },
  { label: 'Logo Preview',    href: '/admin/logo-preview',    icon: Palette },

  // ── Costs (parent) — all cost / spend surfaces under one menu ──
  { label: 'Costs', icon: DollarSign, children: [
    { label: 'Vendor Runway',  href: '/admin/runway',                 icon: DollarSign },
    { label: 'App Installs',   href: '/admin/app-installs',           icon: Smartphone },
    { label: 'Cost Tracking',  href: '/admin/infrastructure-pricing', icon: Table2 },
    { label: 'Payouts',        href: '/admin/payouts',                icon: Banknote },
  ]},

  // ── Audit & Health (parent) — audit trail, health, moderation ──
  { label: 'Audit & Health', icon: ScrollText, children: [
    { label: 'Audit Log',        href: '/admin/audit-log',     icon: ScrollText },
    { label: 'Tenant Health',    href: '/admin/tenant-health', icon: HeartPulse },
    { label: 'Buddy Trip Abuse', href: '/admin/buddy-abuse',   icon: Flag },
  ]},

  { label: 'Announcements',   href: '/admin/announcements',   icon: Megaphone },

  // ── Configuration (parent) — platform config, infra + data policy ──
  { label: 'Configuration', icon: Settings, children: [
    { label: 'AI Settings',    href: '/admin/ai-settings',        icon: Sparkles },
    { label: 'Infrastructure', href: '/admin/infrastructure',     icon: Server },
    { label: 'Data Retention', href: '/admin/retention-defaults', icon: Database },
    { label: 'Email Settings', href: '/admin/email-settings',     icon: Mail },
  ]},

  // ── Support & Docs (parent) ──
  { label: 'Support & Docs', icon: ShieldAlert, children: [
    { label: 'Support', href: '/admin/support', icon: ShieldAlert },
    { label: 'Runbook', href: '/admin/runbook', icon: BookOpen },
  ]},
];

export function SuperAdminLayout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tooltip, setTooltip] = useState<{ label: string; y: number } | null>(null);

  const showTooltip = (label: string, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ label, y: rect.top + rect.height / 2 });
  };
  const hideTooltip = () => setTooltip(null);

  const isActive = (href: string) =>
    href === '/admin' ? loc.pathname === '/admin' : loc.pathname.startsWith(href);

  // Auto-expand groups that contain the active route.
  const initialOpen = (): Set<string> => {
    const s = new Set<string>();
    for (const item of NAV) {
      if (isGroup(item) && item.children.some(c => isActive(c.href))) s.add(item.label);
    }
    return s;
  };
  const [openGroups, setOpenGroups] = useState<Set<string>>(initialOpen);
  useEffect(() => { setOpenGroups(initialOpen()); /* eslint-disable-next-line */ }, [loc.pathname]);
  const toggleGroup = (label: string) =>
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const userEmail = user?.email || 'Admin';
  const userInitial = userEmail.charAt(0).toUpperCase();

  return (
    <>
    <div className="flex h-screen overflow-hidden bg-surface-50">
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
      <aside className={cn(
        'fixed lg:relative z-50 h-full bg-surface-100 border-r border-overlay/[0.06] flex flex-col transition-all duration-300',
        collapsed ? 'w-[72px]' : 'w-[250px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      )}>
        {/* Logo */}
        <div className={cn('flex items-center h-16 px-4 border-b border-overlay/[0.06]', collapsed ? 'justify-center' : 'gap-3')}>
          <img src="/logo.svg" alt="DiscoveryGem" className="w-8 h-8 shrink-0 dark:brightness-0 dark:invert" />
          {!collapsed && (
            <div>
              <div className="font-semibold text-surface-900 text-sm">DiscoveryGem</div>
              <div className="text-[10px] text-surface-500 uppercase tracking-widest font-medium">Platform</div>
            </div>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map(item => {
            // ── Group header ──
            if (isGroup(item)) {
              const Icon        = item.icon;
              const childActive = item.children.some(c => isActive(c.href));
              const isOpen      = openGroups.has(item.label);
              return (
                <div key={item.label} className="space-y-0.5">
                  <div
                    onMouseEnter={(e) => collapsed && showTooltip(item.label, e)}
                    onMouseLeave={hideTooltip}
                  >
                    <button
                      onClick={() => toggleGroup(item.label)}
                      className={cn(
                        'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                        childActive
                          ? 'text-brand-600 dark:text-brand-400'
                          : 'text-surface-500 hover:text-surface-800 hover:bg-overlay/[0.04]',
                        collapsed && 'justify-center px-0',
                      )}
                    >
                      <Icon size={18} className={childActive ? 'text-brand-500' : ''} />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          {isOpen
                            ? <ChevronDown  size={14} className="text-surface-400" />
                            : <ChevronRight size={14} className="text-surface-400" />}
                        </>
                      )}
                    </button>
                  </div>
                  {!collapsed && isOpen && (
                    <div className="space-y-0.5 ml-2 pl-3 border-l border-overlay/[0.08]">
                      {item.children.map(child => {
                        const ChildIcon = child.icon;
                        const active    = isActive(child.href);
                        return (
                          <Link key={child.href} to={child.href} onClick={() => setMobileOpen(false)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all duration-200',
                              active
                                ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8 shadow-[inset_3px_0_0_0_#2AB3B1]'
                                : 'text-surface-500 hover:text-surface-800 hover:bg-overlay/[0.04]',
                            )}>
                            <ChildIcon size={14} className={active ? 'text-brand-500' : ''} />
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  {/* When sidebar is collapsed, show children as a flat
                      flyout under the parent (rendered always so a
                      collapsed sidebar still surfaces the routes). */}
                  {collapsed && item.children.map(child => {
                    const ChildIcon = child.icon;
                    const active    = isActive(child.href);
                    return (
                      <div key={child.href}
                        onMouseEnter={(e) => showTooltip(child.label, e)}
                        onMouseLeave={hideTooltip}
                      >
                        <Link to={child.href} onClick={() => setMobileOpen(false)}
                          className={cn(
                            'flex items-center justify-center px-0 py-2 rounded-xl text-sm transition-all duration-200',
                            active
                              ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8'
                              : 'text-surface-400 hover:text-surface-700 hover:bg-overlay/[0.04]',
                          )}>
                          <ChildIcon size={14} />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Leaf entry ──
            const Icon   = item.icon;
            const active = isActive(item.href);
            return (
              <div
                key={item.href}
                onMouseEnter={(e) => collapsed && showTooltip(item.label, e)}
                onMouseLeave={hideTooltip}
              >
                <Link to={item.href} onClick={() => setMobileOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                    active
                      ? 'text-brand-600 dark:text-brand-400 bg-brand-500/8 shadow-[inset_3px_0_0_0_#2AB3B1]'
                      : 'text-surface-500 hover:text-surface-800 hover:bg-overlay/[0.04]',
                    collapsed && 'justify-center px-0',
                  )}>
                  <Icon size={18} className={active ? 'text-brand-500' : ''} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-overlay/[0.06] space-y-1">
          <div
            onMouseEnter={(e) => collapsed && showTooltip('Expand', e)}
            onMouseLeave={hideTooltip}
          >
            <button onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 rounded-xl text-surface-500 hover:text-surface-700 hover:bg-surface-200/60 transition-all text-sm">
              <ChevronLeft size={16} className={cn('transition-transform', collapsed && 'rotate-180')} />
              {!collapsed && <span>Collapse</span>}
            </button>
          </div>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <div
                onMouseEnter={(e) => showTooltip(userEmail, e)}
                onMouseLeave={hideTooltip}
              >
                <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-xs font-bold">{userInitial}</div>
              </div>
              <div
                onMouseEnter={(e) => showTooltip('Sign out', e)}
                onMouseLeave={hideTooltip}
              >
                <button onClick={handleSignOut} className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors">
                  <LogOut size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-500 text-xs font-bold">{userInitial}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-surface-900 truncate">{userEmail}</div>
                <div className="text-[10px] text-surface-500">Super Admin</div>
              </div>
              <button onClick={handleSignOut} title="Sign out" className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-700 transition-colors">
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── MAIN ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-overlay/[0.04] bg-surface-0/60 backdrop-blur-md shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-surface-100">
              <Menu size={20} className="text-surface-600" />
            </button>
            <div className="relative hidden sm:block">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
              <input type="text" placeholder="Search tenants, domains..."
                className="w-72 pl-9 pr-4 py-1.5 text-sm bg-surface-50 border border-overlay/[0.06] rounded-lg text-surface-900 placeholder-surface-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* "Signed in as" pill — makes the active role/context
                visible. Especially useful for super-admins who can
                also be tenant admins; the pill removes any ambiguity
                about which view they're operating in. */}
            <SignedInPill />
            <button onClick={toggleTheme} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              className="p-2 rounded-lg hover:bg-surface-200/60 text-surface-500 hover:text-surface-700 transition-colors">
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="relative p-2 rounded-lg hover:bg-surface-200/60 text-surface-500 hover:text-surface-700 transition-colors">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-500 rounded-full" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 lg:p-8 max-w-[1400px]">{children}</div>
        </main>
      </div>
    </div>

    {/* Fixed tooltip — rendered outside overflow containers */}
    {collapsed && tooltip && (
      <div
        className="fixed z-[9999] pointer-events-none px-2.5 py-1.5 rounded-lg bg-surface-900 dark:bg-surface-0 text-white dark:text-surface-900 text-xs font-medium whitespace-nowrap shadow-lg"
        style={{ left: 82, top: tooltip.y, transform: 'translateY(-50%)' }}
      >
        {tooltip.label}
        <span className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-surface-900 dark:border-r-surface-0" />
      </div>
    )}
    </>
  );
}
